import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { createPortal } from 'react-dom';
import { buildHubWorkbook } from '../utils/excelExport';
import { apiInFlight } from '../api/client';

/**
 * Bouton « Excel » d'un hub : exporte TOUS les onglets d'un coup
 * (1 onglet Palestre = 1 feuille Excel), toujours en CHF.
 *
 * Pour lire des onglets non affichés, on les monte hors écran le temps de
 * l'export (chacun charge ses données), on attend la fin du chargement, puis
 * on convertit les <table> rendus.
 *
 * Props :
 *   tabs       — [{ label, render: () => ReactNode }] (render = sous-page embedded, unité CHF)
 *   fileBase   — base du nom de fichier (ex. nom du hub)
 *   tenantName — nom de l'entreprise (slug dans le fichier)
 *   year       — année active
 *   addToast   — notification (optionnelle)
 */
/* Cœur réutilisable de l'export Excel d'un hub : `run()` monte les onglets hors
   écran (`stage`, à rendre par l'appelant) puis convertit leurs <table>. Permet
   de déclencher l'export depuis AILLEURS que le bouton local — ex. le bouton
   export du coin bas-gauche (registre pageExports, 2026-08-16). */
export function useHubExcelExport({ tabs, fileBase, tenantName, year, addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const [exporting, setExporting] = useState(false);
  const stageRef = useRef(null);

  useEffect(() => {
    if (!exporting) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const settled = await waitReady(stageRef.current);
        if (cancelled) return;
        const n = await buildHubWorkbook(stageRef.current, { fileBase, tenantName, year });
        if (n === 0 && addToast) addToast(t('phrases:p_050163ec40'), 'error');
        else if (!settled && addToast) addToast(p('Export incomplet : des onglets n’avaient pas fini de charger.'), 'error');
      } catch (e) {
        if (addToast) addToast(p('Erreur export Excel'), 'error');
        else console.error(e); // eslint-disable-line no-console
      } finally {
        if (!cancelled) setExporting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [exporting]); // eslint-disable-line

  const stage = exporting ? createPortal(
    <div
      ref={stageRef}
      aria-hidden
      className="excel-export-stage"
      style={{ position: 'fixed', left: -100000, top: 0, width: 1400, opacity: 0, pointerEvents: 'none', zIndex: -1 }}
    >
      {tabs.map((tab) => (
        <div key={tab.label} data-export-tab={tab.label}>{tab.render()}</div>
      ))}
    </div>,
    document.body,
  ) : null;

  /* `run` STABLE : les pages la déposent dans le registre d'exports via un
     useMemo — une fonction recréée à chaque rendu y ferait tourner l'effet en
     boucle. L'export lit les onglets du rendu courant (setExporting déclenche
     un nouveau rendu, `stage` s'y construit), donc rien ne se fige. */
  const run = useCallback(() => setExporting(true), []);
  return { run, exporting, stage };
}

/* Variante « vue courante », pour une section SANS onglets : la page est déjà à
   l'écran, on convertit son DOM RENDU au lieu d'en monter une seconde copie hors
   écran. Deux raisons — pas de second chargement des données, et aucun risque de
   déclencher une sauvegarde au montage d'un écran éditable (les plans de Ventes
   enregistrent au fil de la frappe).

   Props : ref (conteneur rendu — cf. `exportRef` de PageShell), sheetName (nom
   de la feuille), fileBase / tenantName / year (nom de fichier), addToast.

   Comme `useHubExcelExport`, `run` garde son identité d'un rendu à l'autre : la
   page le met dans le tableau qu'elle déclare au registre, et un tableau recréé
   à chaque rendu ferait réécrire le registre en boucle. La configuration passe
   donc par une référence, rafraîchie à chaque rendu. */
export function useViewExcelExport({ ref, sheetName, fileBase, tenantName, year, addToast }) {
  const { t } = useTranslation();
  const p = useP();
  const cfg = useRef(null);
  cfg.current = { ref, sheetName, fileBase, tenantName, year, addToast, t, p };

  const run = useCallback(async () => {
    const c = cfg.current;
    try {
      const n = await buildHubWorkbook(c.ref?.current, {
        fileBase: c.fileBase, tenantName: c.tenantName, year: c.year, sheetName: c.sheetName,
      });
      if (n === 0 && c.addToast) c.addToast(c.t('phrases:p_050163ec40'), 'error');
    } catch (e) {
      if (c.addToast) c.addToast(c.p('Erreur export Excel'), 'error');
      else console.error(e); // eslint-disable-line no-console
    }
  }, []);

  return { run };
}

export default function ExcelExportButton({ tabs, fileBase, tenantName, year, addToast, label = 'Excel' }) {
  const { t } = useTranslation();
  const { run, exporting, stage } = useHubExcelExport({ tabs, fileBase, tenantName, year, addToast });

  return (
    <>
      <button
        type="button"
        className="hr-tabaside-btn hr-tabaside-btn--icon"
        onClick={run}
        disabled={exporting}
        title={t('phrases:p_e05c4bf2d6')}
      >
        {exporting ? '…' : <><DownloadIcon /> {label}</>}
      </button>
      {stage}
    </>
  );
}

// Icône téléchargement (SVG inline, couleur héritée)
function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: '-2px' }}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/* Attend que les onglets montés hors écran aient fini de charger et de rendre.
   Retourne `true` si l'écran s'est stabilisé, `false` si le plafond a été atteint
   (quelque chose est bloqué) — l'appelant PRÉVIENT alors que le classeur peut
   être incomplet.

   Le signal est le RÉSEAU, et lui seul : `apiInFlight()` dit combien de requêtes
   sont en vol. Trois tentatives fondées sur le DOM ont échoué, chacune pour une
   raison différente, et c'est le même défaut de méthode :
     · « un <table> quelque part » → vrai dès l'onglet le plus rapide rendu ;
     · « chaque onglet a sa table » → un onglet légitimement vide (aucune charge
       sociale saisie) n'en a jamais, l'attente allait au plafond ;
     · « rien ne charge » → chaque page nomme son attente autrement
       (`page-table-loading`, `fps-loading`, `iap-empty`, ou rien) — et pire,
       plusieurs réutilisent la MÊME classe pour dire « aucune donnée », si bien
       qu'un écran au repos passait pour un écran occupé.

   On attend donc : réseau au repos ET contenu stable pendant `settleMs`, le
   temps que React rende la dernière réponse. `isBusy` est injectable pour les
   tests. */
export async function waitReady(stage, { maxMs = 20000, settleMs = 400, isBusy = apiInFlight } = {}) {
  if (!stage) return true;
  const started = Date.now();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  let fingerprint = null;
  let quietSince = null;

  while (Date.now() - started < maxMs) {
    const busy = isBusy() > 0;
    // Empreinte du rendu : tables ET volume de texte — toute arrivée de données
    // la fait bouger, et relance donc le délai de calme.
    const print = `${stage.querySelectorAll('table').length}:${(stage.textContent || '').length}`;

    if (busy || print !== fingerprint) {
      fingerprint = print;
      quietSince = busy ? null : Date.now();
    } else {
      if (quietSince == null) quietSince = Date.now();
      if (Date.now() - quietSince >= settleMs) return true;
    }
    await sleep(120); // eslint-disable-line no-await-in-loop
  }
  return false;
}
